import * as GC from '@grapecity/spread-sheets';
import { IBetaEstimateResult } from '@models/beta/beta-estimate';
import { IMarketRate } from '@models/beta/marketrate';
import { getMonthDateString } from '@shared/dates';
import { round } from '@shared/numbers';
import { borders, colours, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { getMarketRateNameById } from '../helpers';

export const addBetaEstimatesToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  estimates: IBetaEstimateResult[],
  marketRates: IMarketRate[]
): number => {
  try {
    if (estimates && estimates.length > 0) {
      rowIndex = createHeaders(sheet, rowIndex, startCol);
      estimates.forEach((est, ind) => {
        rowIndex = populateEstimate(sheet, rowIndex, startCol, est, marketRates);
      });
    }
  } catch (err) {
    console.error('Unable to export the Beta Estimate Output', { sheet, estimates, marketRates, rowIndex }, err);
  }

  return rowIndex;
};

function createHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number): number {
  let takenCols = 0;
  const rowSpan = 2;
  const colSpan = 1;

  sheet.getCell(rowIndex, startCol + takenCols).text('Predicted Coupon');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
  takenCols += colSpan;

  sheet.getCell(rowIndex, startCol + takenCols).text('Rate');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('Start\nDate');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('End\nDate');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('Lag');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols += colSpan;

  sheet.getCell(rowIndex, startCol + takenCols).text('Coefficient');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('S.E.');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('T-Stat');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('R^2 & S.E.');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
  takenCols++;

  sheet.getCell(rowIndex, startCol + takenCols).text('F-STAT & D.F.');
  sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 2);
  takenCols += 2;

  const takenRows = rowSpan;

  // Styling
  const headerArea = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
  headerArea
    .backColor('#ECEEF3')
    .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
    .wordWrap(true)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center);
  headerArea.setBorder(borders.BLACK_MEDIUM, { all: true });

  rowIndex += takenRows;

  return rowIndex;
}

function populateEstimate(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  estimate: IBetaEstimateResult,
  marketRates: IMarketRate[]
): number {
  try {
    const roundingAmount = 4;
    let takenCols = 0;
    const rowSpan = 2;
    let colSpan = 1;
    let takenRows = 0;

    // Predicted Coupon
    sheet.getCell(rowIndex, startCol + takenCols).text(estimate.Name);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;

    // Market Rate Name
    sheet.getCell(rowIndex, startCol + takenCols).text(getMarketRateNameById(estimate.MarketRateId, marketRates));
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // Start Date
    sheet.getCell(rowIndex, startCol + takenCols).text(getMonthDateString(estimate.PeriodStartDate));
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // End Date
    sheet.getCell(rowIndex, startCol + takenCols).text(getMonthDateString(estimate.PeriodEndDate));
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // Lag
    sheet.getCell(rowIndex, startCol + takenCols).text(`${estimate.Lag}`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    colSpan = 2;

    // Coefficient
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(`β0: ${round(estimate.Intercept * 100, roundingAmount)}\nβ1: ${round(estimate.Slope, roundingAmount)}`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // StandardError (S.E.)
    const standardError = `${round(estimate.InterceptStandardError * 100, 4)}\n${round(estimate.SlopeStandardError, 4)}`;
    sheet.getCell(rowIndex, startCol + takenCols).text(standardError);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // TStat
    const TStat = `${round(estimate.InterceptTStat, 4)}\n${round(estimate.SlopeTStat, 4)}`;
    sheet.getCell(rowIndex, startCol + takenCols).text(TStat);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // R^2 & S.E.
    sheet.getCell(rowIndex, startCol + takenCols).text(`R^2: ${round(estimate.RSquared, 4)}\nS.E.: ${round(estimate.StandardError, 4)}`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 1);
    takenCols++;

    // F-Stat & D.F.
    sheet.getCell(rowIndex, startCol + takenCols).text(`F-STAT: ${round(estimate.FStat, 4)}\nD.F.: ${round(estimate.DegreeFreedom, 4)}`);
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, 2);
    takenCols += 2;

    takenRows += rowSpan;

    // Styling
    const estimateRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
    estimateRange
      .backColor(colours.BLUE)
      .wordWrap(true)
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center);
    estimateRange.setBorder(borders.BLACK_MEDIUM, { all: true });

    rowIndex += takenRows;
  } catch (err) {
    console.error(`Unable to populate Beta Estimate Output`, err, { sheet, estimate, marketRates, rowIndex });
  }

  return rowIndex;
}
